/**
 * routePrefetch — hover/focus-driven warmup for the heaviest lazy route chunks.
 *
 * Why (R29 in ARCHITECTURE.md):
 * `lazyRetry` defers chunk download until the route is actually rendered.
 * For the largest pages (e.g. `FieldPlanningPage` ~1700 LOC) that means a
 * visible delay between the click and the first paint, which users perceive
 * as "the menu didn't respond". Calling `Component.preload()` while the
 * pointer is still over the menu link gives webpack time to fetch and parse
 * the chunk before the click — most clicks then feel instant.
 *
 * Usage from menu components:
 *   import { prefetchRoute } from '../../routes/routePrefetch';
 *   <a onMouseEnter={() => prefetchRoute(path)} onFocus={() => prefetchRoute(path)} />
 *
 * Notes:
 * - All entries are imported lazily (`() => import('./lazyComponents')`) so
 *   simply importing this file does NOT pull the heavy chunks in.
 * - `preload()` is a no-op after the first successful resolution (webpack
 *   caches resolved dynamic imports), so spamming hovers is cheap.
 * - We intentionally ship a small allow-list rather than registering every
 *   route: prefetch is most valuable for chunks > ~100 KB or pages with
 *   large dependency trees. Adding more entries is welcome — just keep the
 *   `import` lazy.
 */

const prefetched = new Set();

/**
 * Map of route path → loader. Each loader is itself lazy: it imports
 * `lazyComponents` only when the user hovers a relevant menu entry.
 */
const PREFETCHERS = {
  // Drilling — primary /drilling/* paths (post-2026-05-25 namespace).
  '/projects/combined-progress': () =>
    import('./lazyComponents').then((m) => m.CombinedProgressMap?.preload?.()),
  '/survey/map': () =>
    import('./lazyComponents').then((m) => m.SurveyMapPage?.preload?.()),
  '/survey/planning': () =>
    import('./lazyComponents').then((m) => m.SurveyPlanningPage?.preload?.()),
  '/survey/production-reports': () =>
    import('./lazyComponents').then((m) => m.SurveyProductionReportsPage?.preload?.()),
  '/recording/map': () =>
    import('./lazyComponents').then((m) => m.RecordingMapPage?.preload?.()),
  '/recording/planning': () =>
    import('./lazyComponents').then((m) => m.RecordingPlanningPage?.preload?.()),
  '/recording/production-reports': () =>
    import('./lazyComponents').then((m) => m.RecordingProductionReportsPage?.preload?.()),
  '/drilling/dashboard': () =>
    import('./lazyComponents').then((m) => m.DrillingOperationsDashboardPage?.preload?.()),
  '/drilling/progress-map': () =>
    import('./lazyComponents').then((m) => m.FieldOperations?.preload?.()),
  '/drilling/work-planning': () =>
    import('./lazyComponents').then((m) => m.FieldPlanningPage?.preload?.()),
  '/drilling/work-projection': () =>
    import('./lazyComponents').then((m) => m.WorkProjectionPage?.preload?.()),
  '/drilling/sp-registry': () =>
    import('./lazyComponents').then((m) => m.SPRegistryPage?.preload?.()),
  '/drilling/sp-registry/map': () =>
    import('./lazyComponents').then((m) => m.MapView?.preload?.()),
  '/drilling/crew-reports': () =>
    import('./lazyComponents').then((m) => m.CrewReportsPage?.preload?.()),
  '/drilling/npt-log': () =>
    import('./lazyComponents').then((m) => m.LostTimePage?.preload?.()),
  '/drilling/client-daily-report': () =>
    import('./lazyComponents').then((m) => m.ClientDailyReportPage?.preload?.()),
  '/drilling/cdr-archive': () =>
    import('./lazyComponents').then((m) => m.CDRArchivePage?.preload?.()),
  '/drilling/npt-report-archive': () =>
    import('./lazyComponents').then((m) => m.NPTReportArchivePage?.preload?.()),
  // Cross-area operations.
  '/operations/helicopter': () =>
    import('./lazyComponents').then((m) => m.HelicopterOps?.preload?.()),
  '/operations/fuel-control': () =>
    import('./lazyComponents').then((m) => m.FuelControlMain?.preload?.()),
  '/finance/petty-cash': () =>
    import('./lazyComponents').then((m) => m.PettyCashDashboard?.preload?.()),
  '/finance/petty-cash/funds': () =>
    import('./lazyComponents').then((m) => m.PettyCashFunds?.preload?.()),
  '/finance/petty-cash/reports': () =>
    import('./lazyComponents').then((m) => m.PettyCashReports?.preload?.()),
  '/finance/petty-cash/monthly-report': () =>
    import('./lazyComponents').then((m) => m.PettyCashMonthlyReport?.preload?.()),
  '/finance/corporate-expenses': () =>
    import('./lazyComponents').then((m) => m.CorporateExpensesList?.preload?.()),
  '/finance/budget': () =>
    import('./lazyComponents').then((m) => m.BudgetDashboard?.preload?.()),
  '/finance/coa': () =>
    import('./lazyComponents').then((m) => m.COAManager?.preload?.()),
  '/finance/exchange-rates': () =>
    import('./lazyComponents').then((m) => m.ExchangeRatesManager?.preload?.()),
  '/inventory': () =>
    import('./lazyComponents').then((m) => m.InventoryDashboard?.preload?.()),
  '/catering/estimator': () =>
    import('./lazyComponents').then((m) => m.CateringHub?.preload?.()),
  '/catering/food-items': () =>
    import('./lazyComponents').then((m) => m.CateringHub?.preload?.()),
  '/catering/rations': () =>
    import('./lazyComponents').then((m) => m.CateringHub?.preload?.()),
  '/catering/estimates': () =>
    import('./lazyComponents').then((m) => m.CateringHub?.preload?.()),
  '/qhse': () =>
    import('./lazyComponents').then((m) => m.QHSEMainPage?.preload?.()),
  '/projects': () =>
    import('./lazyComponents').then((m) => m.ProjectList?.preload?.()),
  '/master-budget': () =>
    import('./lazyComponents').then((m) => m.MasterBudget?.preload?.()),
  '/admin/users': () =>
    import('./lazyComponents').then((m) => m.UserManager?.preload?.()),
  '/admin/access-control': () =>
    import('./lazyComponents').then((m) => m.AccessCtrlMgr?.preload?.()),
  '/admin/backups': () =>
    import('./lazyComponents').then((m) => m.BackupsPage?.preload?.()),
  '/company/backups': () =>
    import('./lazyComponents').then((m) => m.CompanyBackupPage?.preload?.()),
  // HR / Payroll — heavy list + modal dependencies.
  '/hr/payroll/project': () =>
    import('./lazyComponents').then((m) => m.ProjectPayroll?.preload?.()),
  '/hr/payroll/company': () =>
    import('./lazyComponents').then((m) => m.CorporatePayroll?.preload?.()),
  '/hr/company-payroll': () =>
    import('./lazyComponents').then((m) => m.CompanyPayroll?.preload?.()),
  // El menu apunta a `/hr/timesheet/month` y `/hr/personnel` desde la etapa 5a.
  // Las dos viejas siguen aqui: las rutas siguen vivas y alguien puede llegar
  // por un marcador -- precargarlas no cuesta nada y quitarlas si.
  '/hr/timesheet/month': () =>
    import('./lazyComponents').then((m) => m.TimesheetMonth?.preload?.()),
  '/hr/personnel': () =>
    import('./lazyComponents').then((m) => m.PersonnelList?.preload?.()),
  '/hr/timesheet/company': () =>
    import('./lazyComponents').then((m) => m.CompanyTimesheet?.preload?.()),
  '/hr/timesheet': () =>
    import('./lazyComponents').then((m) => m.PersonnelTimesheet?.preload?.()),
  // Camp layout — floor plan + symbols chunk.
  '/basecamp/camp-layout': () =>
    import('./lazyComponents').then((m) => m.CampLayout?.preload?.()),
};

/**
 * Trigger prefetch for the given path. Path may include a query string;
 * we strip it before lookup. Unknown paths are ignored silently.
 */
export const prefetchRoute = (rawPath) => {
  if (!rawPath || typeof rawPath !== 'string') return;
  const path = rawPath.split('?')[0].split('#')[0];
  if (prefetched.has(path)) return;
  const loader = PREFETCHERS[path];
  if (!loader) return;
  prefetched.add(path);
  Promise.resolve()
    .then(() => loader())
    .catch(() => {
      prefetched.delete(path);
    });
};

/**
 * Returns true if a prefetcher exists for the given path. Useful for menu
 * components that want to attach hover handlers conditionally.
 */
export const hasPrefetcher = (rawPath) => {
  if (!rawPath || typeof rawPath !== 'string') return false;
  const path = rawPath.split('?')[0].split('#')[0];
  return Object.prototype.hasOwnProperty.call(PREFETCHERS, path);
};
